import { Heading } from "@/components/ui/typography";
import { AlertCircle, AlertTriangle, Mail, Shield, XCircle } from "lucide-react";
import { Component, ErrorInfo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import { AdminPreloader } from "@/components/admin/AdminPreloader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { UnifiedDashboardSkeleton } from "@/components/skeletons";
import { useAdmin } from "@/contexts/AdminProvider";
import { AdminInlineLoading } from "@/features/admin/components/AdminLoading";
import { useAccessGate } from "@/hooks/useAccessGate";
import { useSession } from "@/hooks/useSession";
import { DEV_ADMIN_CONFIG } from "@/lib/dev/auto-admin";
import { setAdminGuardShell } from "@/lib/monitoring/admin";
import { getCurrentSessionInfo } from "@/lib/rpc/auth/session";

export type RouteGuardMode = "customer" | "admin" | "feature";

export interface RouteGuardMeta {
  policy?: string;
  reason?: string;
  reasonCode?: string;
  sessionStatus?: "loading" | "authed" | "anon" | "recovering";
  authed?: boolean;
  gate?: ReturnType<typeof useAccessGate>["gate"];
  admin?: {
    isAdmin: boolean;
    canPerformAdminActions: boolean;
    isLoading: boolean;
    failureCount: number;
    lastErrorCode: string | null;
    error: string | null;
  };
  bypass?: {
    type: "demo" | "emergency-admin" | "emergency-user";
  };
  redirectTo?: string;
  sessionCheckComplete?: boolean;
}

export interface RouteGuardState {
  status: "loading" | "allowed" | "blocked";
  view?: ReactNode;
  meta: RouteGuardMeta;
  error?: unknown;
}

export interface RouteGuardHookOptions {
  policy?: string;
}

type RouteGuardHook = (options: RouteGuardHookOptions) => RouteGuardState;

export class AdminGuardErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[AdminGuardErrorBoundary] Caught render error:", error, errorInfo);
    console.error("[AdminGuardErrorBoundary] Error stack:", error.stack);
    console.error("[AdminGuardErrorBoundary] Component stack:", errorInfo.componentStack);
    
    // Log additional context for debugging
    if (error.message?.includes('window') || error.message?.includes('document')) {
      console.error("[AdminGuardErrorBoundary] SSR-related error detected - window/document access issue");
    }
    
    // Log error to Sentry or other error tracking service
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        tags: { component: 'AdminGuardErrorBoundary' },
        extra: { componentStack: errorInfo.componentStack }
      });
    }
  }

  render() {
    if (this.state.hasError) {
      // 🛡️ DEBUG: Log when error boundary is triggered
      if (typeof window !== 'undefined') {
        console.error('[AdminGuardErrorBoundary] Rendering error fallback:', {
          error: this.state.error?.message,
          errorStack: this.state.error?.stack,
        });
      }
      
      return (
        this.props.fallback || (
          <main
            role="main"
            aria-labelledby="admin-guard-error-heading"
            className="min-h-screen bg-background flex items-center justify-center px-4"
          >
            <div className="text-center space-y-4 max-w-md p-6 bg-card rounded-lg shadow focus-within:ring-2 focus-within:ring-blue-500">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" aria-hidden="true" />
              <Heading level={1} id="admin-guard-error-heading" className="text-heading-md">
                Admin Guard Error
              </Heading>
              <p className="text-sm text-muted-foreground">
                We couldn&apos;t confirm admin access right now. Refresh the page to try again.
              </p>
              {this.state.error && (
                <details className="text-xs text-muted-foreground mt-2 text-left">
                  <summary className="cursor-pointer">Error details</summary>
                  <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
                    {this.state.error.message}
                    {'\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
              <Button onClick={() => window.location.reload()} className="px-4 py-2">
                Refresh Page
              </Button>
            </div>
          </main>
        )
      );
    }
    return this.props.children;
  }
}

const accessGateIconMap = {
  NOT_AUTHENTICATED: AlertCircle,
  EMAIL_UNVERIFIED: Mail,
  KYC_REQUIRED: Shield,
  SUSPENDED: XCircle,
} as const;

const accessGateMessageMap = {
  NOT_AUTHENTICATED: "This page requires you to sign in",
  EMAIL_UNVERIFIED: "Please verify your email to continue",
  KYC_REQUIRED: "Verification required to access this feature",
  SUSPENDED: "Your account is temporarily suspended",
} as const;

/**
 * loadingView — CANON 5.3/5.4: Single unified skeleton
 * No "Verifying authentication..." text - just clean skeleton
 * Skeleton includes sidebar to match final layout (no CLS)
 */
const loadingView = (
  <main
    role="main"
    aria-busy="true"
    aria-live="polite"
    aria-label="Loading dashboard"
  >
    <UnifiedDashboardSkeleton withSidebar={true} />
  </main>
);

const guardRegistry: Record<RouteGuardMode, RouteGuardHook> = {
  customer: useCustomerGuard,
  admin: useAdminGuard,
  feature: useFeatureGuard,
};


const guardWrappers: Partial<Record<RouteGuardMode, (props: { children: ReactNode }) => React.JSX.Element>> = {
  admin: ({ children }) => (
    <AdminGuardErrorBoundary>{children}</AdminGuardErrorBoundary>
  ),
};

export function getRouteGuardWrapper(mode: RouteGuardMode) {
  return guardWrappers[mode];
}

export function useRouteGuard(mode: RouteGuardMode, options: RouteGuardHookOptions = {}): RouteGuardState {
  const hook = guardRegistry[mode];
  if (!hook) {
    console.warn(`[RouteGuard] Unknown mode: ${mode}`);
    return { status: "allowed", meta: { policy: options.policy } };
  }

  return hook(options);
}

function useCustomerGuard(): RouteGuardState {
  const { status } = useSession();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const timeoutRef = useRef<number | null>(null);
  const [emergencyBypass, setEmergencyBypass] = useState(false);

  useEffect(() => {
    const nextShell = status === "authed" ? "authenticated" : "public";
    setAdminGuardShell(nextShell);
    return () => {
      setAdminGuardShell("public");
    };
  }, [status]);

  useEffect(() => {
    let active = true;
    timeoutRef.current = window.setTimeout(() => {
      if (!active) return;
      setAuthed(false);
      setChecking(false);
    }, 5000);

    const checkAuth = async () => {
      try {
        const sessionResult = await getCurrentSessionInfo();
        if (!active) return;
        const hasUser = Boolean(sessionResult.ok && sessionResult.data?.user);
        setAuthed(hasUser);
        setChecking(false);
      } catch (err) {
        if (!active) return;
        console.error("[RouteGuard:customer] Auth check failed:", err);
        setError(err);
        setAuthed(false);
        setChecking(false);
      } finally {
        // Cleanup timeout if component still mounted
        if (active && timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    checkAuth();

    return () => {
      active = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emergencyParam = urlParams.get("emergency_user") === "true";
    const storedBypass = localStorage.getItem("emergency_user_bypass") === "true";
    const bypass = storedBypass || emergencyParam;
    if (emergencyParam) {
      localStorage.setItem("emergency_user_bypass", "true");
    }
    setEmergencyBypass(bypass);
  }, [location.search]);

  if (emergencyBypass) {
    return {
      status: "allowed",
      meta: {
        sessionStatus: status,
        authed: true,
        bypass: { type: "emergency-user" },
        sessionCheckComplete: !checking,
      },
    };
  }

  // E2E Testing Bypass
  if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
    return {
      status: "allowed",
      meta: {
        sessionStatus: "authed",
        authed: true,
        bypass: { type: "demo" },
        sessionCheckComplete: true,
      },
    };
  }

  if (typeof window !== 'undefined') {
    const e2eSyntheticUser = window.localStorage.getItem('e2e_user_id');
    if (e2eSyntheticUser) {
      return {
        status: "allowed",
        meta: {
          sessionStatus: "authed",
          authed: true,
          bypass: { type: "demo" },
          sessionCheckComplete: true,
        },
      };
    }
  }

  const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";

  if (isDemoMode && bypassAuth) {
    return {
      status: "allowed",
      meta: {
        sessionStatus: status,
        authed: true,
        bypass: { type: "demo" },
        sessionCheckComplete: !checking,
      },
    };
  }

  const isLoading = status === "loading" || checking;

  if (isLoading) {
    return {
      status: "loading",
      view: loadingView,
      meta: {
        sessionStatus: status,
        authed: false,
        sessionCheckComplete: !checking,
      },
      error,
    };
  }

  const isAuthed = status === "authed" || authed;

  if (!isAuthed) {
    return {
      status: "blocked",
      view: <Navigate to="/auth" replace state={{ from: location }} />,
      meta: {
        sessionStatus: status,
        authed: false,
        redirectTo: "/auth",
        sessionCheckComplete: !checking,
      },
      error,
    };
  }

  return {
    status: "allowed",
    meta: {
      sessionStatus: status,
      authed: true,
      sessionCheckComplete: !checking,
    },
    error,
  };
}

function useAdminGuard(): RouteGuardState {
  // E2E Testing Bypass - check BOTH localStorage AND cookies
  // Playwright doesn't reliably restore localStorage, so we use cookies as primary method
  const getCookieValue = (name: string): string | null => {
    if (typeof document === 'undefined') return null;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return cookieValue || null;
      }
    }
    return null;
  };

  const hasE2EBypass = typeof window !== 'undefined' && (
    getCookieValue('e2e_admin_bypass') === 'true' ||
    window.localStorage.getItem('e2e_admin_bypass') === 'true'
  );

  // CRITICAL: Call hooks unconditionally at the top (rules of hooks)
  // Both hooks have been updated to return default values instead of throwing
  const sessionResult = useSession();
  const adminResultRaw = useAdmin();

  // Process session result (hooks already called unconditionally above)
  const sessionStatus = sessionResult.status;
  const sessionUser = sessionResult.user;
  // Debug logs removed for PII safety - use React DevTools to inspect state

  // Process admin result (hooks already called unconditionally above)

  // Always call all hooks before any conditional logic to avoid React error #300
  const envConfig = useMemo(() => {
    const isLocalDev = import.meta.env.VITE_RUNTIME_ENV === "development";
    const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true";
    const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";
    return { isLocalDev, isDemoMode, bypassAuth };
  }, []);

  const emergencyBypass = useMemo(() => {
    if (typeof window === "undefined") {
      return { bypass: false, emergencyParam: false };
    }

    const urlParams = new URLSearchParams(window.location.search);
    const emergencyParam = urlParams.get("emergency_admin") === "true";
    const bypass = envConfig.isLocalDev && (localStorage.getItem("emergency_admin_bypass") === "true" || emergencyParam);
    if (emergencyParam) {
      localStorage.setItem("emergency_admin_bypass", "true");
    }
    return { bypass, emergencyParam };
  }, [envConfig.isLocalDev]);

  const hasSessionUser = Boolean(sessionUser?.id);
  const isRecoveringSession = sessionStatus === "recovering";
  const hasTrustedSession = hasSessionUser && (sessionStatus === "authed" || isRecoveringSession);

  // Now safe to do conditional logic after all hooks are called
  const shouldCheckAdmin = hasTrustedSession;

  const baseAdminResult =
    adminResultRaw ??
    ({
      isAdmin: false,
      canPerformAdminActions: false,
      isLoading: false,
      loading: false,
      failureCount: 0,
      lastErrorCode: null,
      retry: () => { },
      userId: null,
      email: null,
      error: null,
    } satisfies ReturnType<typeof useAdmin>);

  const adminResult = shouldCheckAdmin
    ? baseAdminResult
    : {
      ...baseAdminResult,
      isAdmin: false,
      canPerformAdminActions: false,
      isLoading: false,
      loading: false,
    };

  const establishedAdminAccessRef = useRef(false);

  // E2E Testing Bypass - early return AFTER all hooks are called
  if (hasE2EBypass) {
    return {
      status: "allowed",
      meta: {
        sessionStatus,
        authed: true,
        bypass: { type: "emergency-admin" },
        admin: {
          isAdmin: true,
          canPerformAdminActions: true,
          isLoading: false,
          failureCount: adminResult.failureCount ?? 0,
          lastErrorCode: adminResult.lastErrorCode ?? null,
          error: adminResult.error ?? null,
        },
      },
      error: adminResult.error ?? null,
    };
  }

  // Demo mode bypass
  if (envConfig.isLocalDev && envConfig.isDemoMode && envConfig.bypassAuth) {
    return {
      status: "allowed",
      meta: {
        sessionStatus,
        authed: true,
        bypass: { type: "demo" },
        admin: {
          isAdmin: true,
          canPerformAdminActions: true,
          isLoading: false,
          failureCount: adminResult.failureCount ?? 0,
          lastErrorCode: adminResult.lastErrorCode ?? null,
          error: adminResult.error ?? null,
        },
      },
      error: adminResult.error ?? null,
    };
  }

  // Emergency admin bypass
  if (DEV_ADMIN_CONFIG.enabled && DEV_ADMIN_CONFIG.bypassAuth) {
    return {
      status: "allowed",
      meta: {
        sessionStatus,
        authed: true,
        bypass: { type: "emergency-admin" },
        admin: {
          isAdmin: true,
          canPerformAdminActions: true,
          isLoading: false,
          failureCount: adminResult.failureCount ?? 0,
          lastErrorCode: adminResult.lastErrorCode ?? null,
          error: adminResult.error ?? null,
        },
      },
      error: adminResult.error ?? null,
    };
  }

  const hasEstablishedAdminAccess = Boolean(
    adminResult.isAdmin && adminResult.canPerformAdminActions
  );

  if (hasEstablishedAdminAccess) {
    establishedAdminAccessRef.current = true;
  }

  const hadEstablishedAdminAccess = establishedAdminAccessRef.current;
  const isRefreshingAdminAccess = shouldCheckAdmin && adminResult.isLoading;
  const hasEffectiveAdminAccess =
    hasEstablishedAdminAccess ||
    (hadEstablishedAdminAccess && (isRefreshingAdminAccess || isRecoveringSession));

  const adminMeta = {
    isAdmin: hasEffectiveAdminAccess,
    canPerformAdminActions: hasEffectiveAdminAccess,
    isLoading: adminResult.isLoading,
    failureCount: adminResult.failureCount ?? 0,
    lastErrorCode: adminResult.lastErrorCode ?? null,
    error: adminResult.error ?? null,
  };

  const shouldShowLoading =
    sessionStatus === "loading" ||
    (!hadEstablishedAdminAccess && isRecoveringSession) ||
    (!hadEstablishedAdminAccess && isRefreshingAdminAccess);

  if (shouldShowLoading) {
    return {
      status: "loading",
      view: <AdminPreloader variant="loading" />,
      meta: {
        sessionStatus,
        authed: hasTrustedSession,
        admin: adminMeta,
      },
      error: adminResult.error ?? null,
    };
  }

  if (hadEstablishedAdminAccess && (isRecoveringSession || isRefreshingAdminAccess)) {
    return {
      status: "allowed",
      meta: {
        sessionStatus,
        authed: hasTrustedSession,
        admin: adminMeta,
      },
      error: adminResult.error ?? null,
    };
  }

  if (sessionStatus === "anon") {
    return {
      status: "blocked",
      view: (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md p-6">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto" />
            <div className="space-y-2">
              <h2 className="font-display text-heading-md text-gray-900">Authentication Required</h2>
              <p className="text-sm text-gray-600">You need to sign in to access the admin panel.</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => (window.location.href = "/auth")}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Sign In
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      ),
      meta: {
        sessionStatus,
        authed: false,
        redirectTo: "/auth",
        admin: adminMeta,
      },
      error: adminResult.error ?? null,
    };
  }

  if (emergencyBypass.bypass) {
    return {
      status: "allowed",
      meta: {
        sessionStatus,
        authed: true,
        bypass: { type: "emergency-admin" },
        admin: {
          isAdmin: true,
          canPerformAdminActions: true,
          isLoading: false,
          failureCount: adminResult.failureCount ?? 0,
          lastErrorCode: adminResult.lastErrorCode ?? null,
          error: adminResult.error ?? null,
        },
      },
      error: adminResult.error ?? null,
    };
  }

  if (!adminResult.isAdmin || !adminResult.canPerformAdminActions) {
    return {
      status: "blocked",
      view: (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md p-6">
            <AlertTriangle className="h-16 w-16 text-destructive mx-auto" />
            <div className="space-y-2">
              <h2 className="font-display text-heading-md text-gray-900">Access Denied</h2>
              <p className="text-sm text-gray-600">You don't have administrator privileges to access this page.</p>
              {adminResult.lastErrorCode && (
                <p className="text-xs text-muted-foreground mt-2">
                  Error code: {adminResult.lastErrorCode}
                </p>
              )}
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = "/auth";
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Sign in as different user
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Back to Home
              </button>
              {envConfig.isLocalDev && (
                <button
                  onClick={() => {
                    localStorage.setItem("emergency_admin_bypass", "true");
                    window.location.reload();
                  }}
                  className="w-full px-3 py-1 text-xs bg-amber-100 text-amber-800 rounded border border-amber-300 hover:bg-amber-200"
                >
                  🚨 Emergency Bypass (DEV ONLY)
                </button>
              )}
            </div>
          </div>
        </div>
      ),
      meta: {
        sessionStatus,
        authed: hasTrustedSession,
        admin: adminMeta,
      },
      error: adminResult.error ?? null,
    };
  }

  return {
    status: "allowed",
    meta: {
      sessionStatus,
      authed: hasTrustedSession,
      admin: adminMeta,
    },
    error: adminResult.error ?? null,
  };
}

function useFeatureGuard(options: RouteGuardHookOptions): RouteGuardState {
  const routeId = options.policy;

  // Always call the hook to avoid conditional hook calls (React error #300)
  const { gate, isLoading, isAllowed, isBlocked, error } = useAccessGate(
    routeId ?? null,
    { enabled: Boolean(routeId) }
  );

  if (!routeId) {
    return {
      status: "allowed",
      meta: {
        policy: routeId,
      },
    };
  }

  if (isLoading) {
    return {
      status: "loading",
      view: <div className="flex justify-center p-8"><AdminInlineLoading role="status" aria-live="polite" /></div>,
      meta: {
        policy: routeId,
        gate,
      },
      error,
    };
  }

  if (isAllowed || (!isBlocked && !gate)) {
    return {
      status: "allowed",
      meta: {
        policy: routeId,
        gate,
      },
      error,
    };
  }

  const reasonCode = (gate as any)?.reason_code;
  const Icon = reasonCode && accessGateIconMap[reasonCode as keyof typeof accessGateIconMap] ?
    accessGateIconMap[reasonCode as keyof typeof accessGateIconMap] :
    AlertCircle;
  const message = reasonCode && accessGateMessageMap[reasonCode as keyof typeof accessGateMessageMap]
    ? accessGateMessageMap[reasonCode as keyof typeof accessGateMessageMap]
    : "Access restricted";

  return {
    status: "blocked",
    view: (
      <main
        role="main"
        aria-labelledby="access-required-heading"
        className="container max-w-md mx-auto mt-16 p-4"
      >
        <Card className="p-8 text-center">
          <Icon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
          <Heading level={1} id="access-required-heading" className="mb-2">
            Access Required
          </Heading>
          <p className="text-muted-foreground mb-6">{message}</p>

          {(gate as any)?.cta && (
            <Button asChild className="mb-4">
              <Link to={(gate as any).cta.to}>{(gate as any).cta.label}</Link>
            </Button>
          )}

          <p className="text-sm text-muted-foreground">
            Need help?{" "}
            <Link to="/support" className="text-primary hover:underline">
              Contact support
            </Link>
          </p>

          {(gate as any)?.meta?.env && (gate as any).meta.env !== "prod" && (
            <div className="mt-4 text-xs text-muted-foreground">Environment: {(gate as any).meta.env}</div>
          )}
        </Card>
      </main>
    ),
    meta: {
      policy: routeId,
      gate,
      reasonCode,
      reason: message,
    },
    error,
  };
}

